import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Toggle } from "@common";
import { useSpinnerContext, useSessionContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { helpers, Enums } from "@shared";
import { PaymentDay } from ".";
import { PaymentMethodCard } from "./common/payment-method-card";

const MultiAutopay = () => {

    const navigate = useNavigate(), location = useLocation();
    const { setSpinnerShow } = useSpinnerContext();
    const [settings, setSettings] = useState(null);
    const {
        session,
        accounts,
        changeSelectedResident,
    } = useSessionContext();
    const {
        orgDays,
        potDeadline
    } = session;

    const getPayorPaymentMethods = async () => {
        setSpinnerShow(true);
        try {
            const newSettings = {};
            const response = await sunboundPayProxy.getPayorPaymentMethods({ isActive: true, withAccountAssignments: true });

            response.forEach(a => {
                const { accountPaymentMethods = [], sourceName, accountType, last4, disabledAt } = a;
                accountPaymentMethods.forEach(apm => {
                    const { isDefault, hasPOTInvoice, accountPayor: { isAutoPay, dueDay, account: { residentName, idAccount } } } = apm;
                    if (isDefault) {
                        newSettings[idAccount] = {
                            idPaymentMethod: apm.idPaymentMethod,
                            isAutoPay,
                            dueDay,
                            sourceName,
                            accountType,
                            last4,
                            residentName,
                            hasPOTInvoice,
                            disabledAt
                        };
                    }
                });
            });

            setSettings(newSettings);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    useEffect(() => {
        (async () => {
            await getPayorPaymentMethods();
        })();
    }, [session]);

    const toggleAutoPay = async (idAccount) => {

        setSpinnerShow(true);
        try {
            let autodrawDay = null;
            const newSettings = { ...settings };
            const { isAutoPay, dueDay: payorDueDay } = newSettings[idAccount];
            const flipAutoPay = !isAutoPay;

            if (flipAutoPay)
                ({ autodrawDay } = helpers.getDaysFromPayor(orgDays, payorDueDay));

            await sunboundPayProxy.updateAccountPayorPreferences(idAccount, { isAutoPay: flipAutoPay, dueDay: autodrawDay });

            newSettings[idAccount].isAutoPay = flipAutoPay;
            newSettings[idAccount].dueDay = autodrawDay;
            setSettings(newSettings);
        } catch (error) {
            console.error(error);
        } finally {
            setSpinnerShow(false);
        }
    };

    const onClick = () => {
        helpers.navigateNext(location, navigate);
    };

    const dueDayChange = async (idAccount, dueDayOption) => {
        setSpinnerShow(true);
        try {
            const dueDay = parseInt(dueDayOption);
            const newSettings = { ...settings };
            await sunboundPayProxy.updateAccountPayorPreferences(idAccount, { dueDay });
            newSettings[idAccount].dueDay = dueDay
            setSettings(newSettings);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const paymentDay = (idAccount) => {
        const { dueDay: payorDueDay, hasPOTInvoice } = settings[idAccount];
        const { locked, autodrawDay, maxDay } = helpers.getDaysFromPayor(orgDays, payorDueDay, hasPOTInvoice ? Enums.ProductType.SunboundPay : null, potDeadline);

        return (
            <PaymentDay setDueDay={(dueDay) => dueDayChange(idAccount, dueDay)} dueDay={autodrawDay} locked={locked} maxDay={maxDay} />
        );

    };

    const renderAccounts = settings && accounts.map(account => {
        const { idAccount, residentName } = account;
        const accountSettings = settings[idAccount];
        const isAutoPay = accountSettings?.isAutoPay;

        return (
            <div key={idAccount} className="flex flex-col w-full border-2 p-4 rounded-lg gap-y-4">
                <span>Toggle Auto-pay</span>
                <div className="flex flex-row ml-4 py-1 gap-x-5">
                    <Toggle isOn={isAutoPay} enabled={(accountSettings && !accountSettings.disabledAt) || isAutoPay} onClick={() => toggleAutoPay(idAccount)} />
                    <span className="font-bold text-xl my-auto">{residentName}</span>
                </div>
                {
                    accountSettings
                    ? <>
                        {/* by definition because we check for isDefault above, the pm is the dpm */}
                        <div
                            onClick={() => accountSettings.disabledAt && changeSelectedResident(idAccount, () => navigate('/payment-method-settings'))}
                            className={accountSettings.disabledAt ? "cursor-pointer": null}
                        >
                            <PaymentMethodCard paymentMethod={accountSettings} isDefault />
                        </div>
                        <div>
                            {
                                isAutoPay &&
                                paymentDay(idAccount)
                            }
                        </div>
                    </>
                    : (
                        <div
                            className="flex flex-row  p-3 pl-5 rounded-xl border cursor-pointer bg-gray-200 border-gray-300"
                            onClick={() => changeSelectedResident(idAccount, () => navigate('/payment-method-settings'))}
                        >
                            <div className="w-2/3">
                                <div>
                                    <span className="font-bold text-lg">No Payment Method Selected</span>
                                </div>
                                <span>Please add a payment method to this account to use Auto-Pay</span>
                            </div>
                        </div>
                    )
                }
            </div>
        );
    })

    return (
        <div className="page-content-wrapper">
            <h1 className="page-header">Auto-Pay Settings</h1>
            <span className="page-subheader text-left">By turning on Auto-Pay you are authorizing Sunbound to debit your account for your total monthly balance on your payment day. Changes are automatically saved.</span>
            {renderAccounts}
            <button type="button" className="btn btn-primary text-xl mt-4 rounded-full" onClick={onClick}>Continue</button>
        </div>
    );
};

export default MultiAutopay;